import useBlockPotTokenData, { BlockPotTokenState } from '@/hooks/contracts/block-pot-token/useBlockPotTokenData'
import useBlockPotRewardTrackerStateInternal from '@/hooks/contracts/block-pot-reward-tracker/useBlockPotRewardTrackerState'
import { BlockPotRewardTrackerState, LotteryConfig } from '@/types/lottery'
import React, { createContext, useContext, useEffect } from 'react'
import useNextConfig from '@/hooks/contracts/block-pot-config-manager/useNextConfig'
import useActiveProposalTypes, { ActiveProposalTypes } from '@/hooks/contracts/block-pot-config-manager/useActiveProposalTypes'
import { useLottery } from './BlockPotProvider'
import useLoadedGameRounds, { UseLoadedGameRoundsReturnType } from '@/hooks/contracts/lottery/useLoadedGameRounds'
import { useSelectedGame } from './SelectedGameProvider'
import { usePrevious } from '@/hooks/utilities/usePrevious'

type LotteryContextType = {
    blockPotToken?: BlockPotTokenState,
    blockPotRewardTracker?: BlockPotRewardTrackerState,
    lotteryNextConfig?: LotteryConfig,
    activeProposalTypes?: ActiveProposalTypes,
} & UseLoadedGameRoundsReturnType

const LotteryContext = createContext<LotteryContextType | undefined>(undefined)

type Props = {
  children: React.ReactNode
}

export default function LotteryProvider({ children }: Props): React.ReactElement {
    const {loadedRoundIndexes, resetLoadedRoundIndexes, loadOlderRound, loadNewerRound, canLoadNewerRound, canLoadOlderRound } = useLoadedGameRounds()
    const lottery = useLottery()
    const blockPotToken = useBlockPotTokenData()
    const blockPotRewardTracker = useBlockPotRewardTrackerStateInternal()
    const lotteryNextConfig = useNextConfig()
    const activeProposalTypes = useActiveProposalTypes()

    const { selectedGame } = useSelectedGame()
    const previousSelectedGame = usePrevious(selectedGame)

    const lotteryContextValue = {
        lottery,
        blockPotToken,
        blockPotRewardTracker, 
        lotteryNextConfig,
        activeProposalTypes,
        loadedRoundIndexes,
        resetLoadedRoundIndexes,
        loadOlderRound,
        loadNewerRound,
        canLoadNewerRound,
        canLoadOlderRound
    }

    useEffect(() => {
        if (previousSelectedGame != selectedGame) {
            resetLoadedRoundIndexes()
        }
    }, [previousSelectedGame, selectedGame, resetLoadedRoundIndexes])

    return (
        <LotteryContext.Provider value={lotteryContextValue}>
            {children}
        </LotteryContext.Provider>
    )
}

const useLotteryContext = () => {
    const context = useContext(LotteryContext)
    if (!context) {
        throw new Error('BlockPot hooks must be used within BlockPotProvider')
    }
    return context
}

export const useBlockPotTokenState = () => {
    return useLotteryContext().blockPotToken
}

export const useBlockPotRewardTrackerState = () => {
    return useLotteryContext().blockPotRewardTracker
}

export const useGovernance = () => {
    const { activeProposalTypes, lotteryNextConfig } = useLotteryContext()
    return {
        activeProposalTypes,
        lotteryNextConfig
    }
}

export const useLoadedRoundIndexes = () => {
    const { loadedRoundIndexes, loadNewerRound, loadOlderRound, canLoadOlderRound, canLoadNewerRound } = useLotteryContext()
    return { loadedRoundIndexes, loadNewerRound, loadOlderRound, canLoadOlderRound, canLoadNewerRound }
}